import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

function todayBusinessDate(): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}

interface Props {
  /** Whether the dialog is open. */
  open: boolean;
  /** Where the pedido is going — used in the title. Example: "Em produção". */
  toStatusLabel: string;
  /** Pedido description shown under the title for confirmation. */
  pedidoLabel: string;
  /** Verb prefix for the title. Default "Avançar para". Use e.g. "Voltar para" for a step return. */
  actionLabel?: string;
  /** Called with the chosen date (yyyy-MM-dd). Must throw to keep the modal open. */
  onConfirm: (date: string) => Promise<void>;
  onCancel: () => void;
}

export function AdvanceConfirmDialog({ open, toStatusLabel, pedidoLabel, actionLabel = "Avançar para", onConfirm, onCancel }: Props) {
  const [date, setDate] = useState(todayBusinessDate());
  const [submitting, setSubmitting] = useState(false);

  // Reset the date each time the dialog opens so a leftover value from a
  // previous attempt isn't reused silently.
  useEffect(() => {
    if (open) setDate(todayBusinessDate());
  }, [open]);

  const submit = async () => {
    if (!date) {
      toast.error("Informe a data");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(date);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{actionLabel} {toStatusLabel}</DialogTitle>
          <DialogDescription>{pedidoLabel}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="advance-date">Data da transição</Label>
          <DatePicker id="advance-date" value={date} onChange={setDate} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="hero" size="sm" onClick={() => void submit()} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
